import React, { useState, useEffect, useCallback } from "react";
import {
  Layout,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Input,
  Select,
  Avatar,
  Tooltip,
  Spin,
  DatePicker,
  Tabs,
  Collapse,
  Badge,
  Statistic,
  Divider,
  Empty,
} from "antd";
import {
  FaDollarSign,
  FaBuilding,
  FaUsers,
  FaLayerGroup,
  FaTasks,
  FaClock,
  FaArrowLeft,
  FaChartPie,
  FaUserTie,
  FaInfoCircle,
  FaCalendarAlt,
  FaProjectDiagram,
  FaSearch,
  FaFilter,
  FaChevronDown,
  FaChevronRight,
  FaPauseCircle,
  FaExclamationCircle,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import Api from '../api/Api';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;
const { Panel } = Collapse;

/* ─────────────────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────────────────── */
const formatMoney = (value) => {
  if (value == null || isNaN(value)) return "—";
  return new Intl.NumberFormat("pt-AO", {
    style: "currency",
    currency: "AOA",
    minimumFractionDigits: 2,
  }).format(value);
};

const formatHoras = (horas) => {
  if (horas == null || isNaN(horas)) return "—";
  const h = Math.floor(horas);
  const m = Math.round((horas - h) * 60);
  if (m === 0) return `${h}h`;
  return `${h}h ${m}min`;
};

const formatMinutos = (min) => {
  if (!min) return "—";
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m}min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}min`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return dayjs(dateStr).format("DD/MM/YYYY");
};

/* ─────────────────────────────────────────────────────────────────────────────
   METRIC CARD (reusable)
───────────────────────────────────────────────────────────────────────────── */
const MetricCard = ({ title, value, icon, iconBg, iconColor, subtitle }) => (
  <Card
    bordered={false}
    style={{
      borderRadius: 16,
      boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
      border: "1px solid #f0f0f0",
      height: "100%",
    }}
    bodyStyle={{ padding: "20px 24px" }}
  >
    <Space size="middle" align="start">
      <div
        style={{
          backgroundColor: iconBg,
          borderRadius: 12,
          padding: 12,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {React.cloneElement(icon, { style: { fontSize: 22, color: iconColor } })}
      </div>
      <div>
        <Text style={{ fontSize: 13, color: "#8c8c8c", display: "block" }}>{title}</Text>
        <div style={{ fontSize: 24, fontWeight: 700, color: "#1f1f1f", lineHeight: 1.2, marginTop: 4 }}>
          {value}
        </div>
        {subtitle && (
          <Text style={{ fontSize: 12, color: "#8c8c8c", marginTop: 4, display: "block" }}>
            {subtitle}
          </Text>
        )}
      </div>
    </Space>
  </Card>
);

/* ─────────────────────────────────────────────────────────────────────────────
   SECTION HEADER
───────────────────────────────────────────────────────────────────────────── */
const SectionHeader = ({ icon, title, color, extra }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 16,
    }}
  >
    <Space>
      <div
        style={{
          background: color + "22",
          borderRadius: 8,
          padding: "6px 10px",
          display: "flex",
          alignItems: "center",
        }}
      >
        {React.cloneElement(icon, { style: { color, fontSize: 16 } })}
      </div>
      <Text strong style={{ fontSize: 15 }}>
        {title}
      </Text>
    </Space>
    {extra}
  </div>
);

/* ─────────────────────────────────────────────────────────────────────────────
   ABA: DIREÇÃO
───────────────────────────────────────────────────────────────────────────── */
const TabDirecao = ({ dataInicio, dataFim }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedDir, setExpandedDir] = useState([]);

  const load = useCallback(async () => {
    if (!dataInicio || !dataFim) return;
    try {
      setLoading(true);
      const res = await Api.ListCustoDirecao({ dataInicio, dataFim });
      setData(Array.isArray(res?.data) ? res.data : []);
    } catch {
      toast.error("Erro ao carregar custos por direcção.");
    } finally {
      setLoading(false);
    }
  }, [dataInicio, dataFim]);

  useEffect(() => { load(); }, [load]);

  const totalGeral = data.reduce((a, d) => a + (d.custo_total || 0), 0);
  const totalHoras = data.reduce((a, d) => a + (d.horas_efectivas || 0), 0);
  const totalProjetos = data.reduce((a, d) => a + (d.total_projetos || 0), 0);

  const columnsDir = [
    {
      title: "Direcção",
      dataIndex: "direcao",
      key: "direcao",
      render: (v, record) => (
        <Space>
          <Avatar
            shape="square"
            style={{ background: "#7c3aed22", borderRadius: 8, color: "#7c3aed" }}
            icon={<FaBuilding />}
          />
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{v}</div>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {record.total_projetos} projecto{record.total_projetos !== 1 ? "s" : ""}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Horas Efectivas",
      dataIndex: "horas_efectivas",
      key: "horas",
      align: "right",
      render: (v) => (
        <Space>
          <FaClock style={{ color: "#8c8c8c" }} />
          <Text>{formatHoras(v)}</Text>
        </Space>
      ),
    },
    {
      title: "Custo Total",
      dataIndex: "custo_total",
      key: "custo",
      align: "right",
      sorter: (a, b) => a.custo_total - b.custo_total,
      render: (v) => (
        <Text strong style={{ color: "#7c3aed", fontSize: 14 }}>
          {formatMoney(v)}
        </Text>
      ),
    },
  ];

  if (loading) return <div style={{ textAlign: "center", padding: 60 }}><Spin size="large" /></div>;

  return (
    <div>
      <Row gutter={[20, 20]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <MetricCard
            title="Custo Total"
            value={formatMoney(totalGeral)}
            icon={<FaDollarSign />}
            iconBg="#f3e8ff"
            iconColor="#7c3aed"
            subtitle={`${data.length} direcção(ões)`}
          />
        </Col>
        <Col xs={24} sm={8}>
          <MetricCard
            title="Horas Efectivas"
            value={formatHoras(totalHoras)}
            icon={<FaClock />}
            iconBg="#e6f4ff"
            iconColor="#1677ff"
            subtitle="Total no período"
          />
        </Col>
        <Col xs={24} sm={8}>
          <MetricCard
            title="Projectos"
            value={totalProjetos}
            icon={<FaProjectDiagram />}
            iconBg="#fff7e6"
            iconColor="#fa8c16"
            subtitle="Com custo no período"
          />
        </Col>
      </Row>

      {data.length === 0 ? (
        <Empty description="Nenhum dado para o período selecionado" />
      ) : (
        <Collapse
          activeKey={expandedDir}
          onChange={setExpandedDir}
          expandIconPosition="end"
          style={{ background: "transparent", border: "none" }}
        >
          {data.map((dir) => (
            <Panel
              key={dir.id_direcao}
              style={{
                marginBottom: 16,
                borderRadius: 14,
                border: "1px solid #e8e8f0",
                background: "#fff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                overflow: "hidden",
              }}
              header={
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingRight: 16 }}>
                  <Space>
                    <Avatar shape="square" style={{ background: "#7c3aed22", borderRadius: 8, color: "#7c3aed" }} icon={<FaBuilding />} />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{dir.direcao}</div>
                      <Text type="secondary" style={{ fontSize: 12 }}>{dir.total_projetos} projecto{dir.total_projetos !== 1 ? "s" : ""} · {formatHoras(dir.horas_efectivas)}</Text>
                    </div>
                  </Space>
                  <Text strong style={{ color: "#7c3aed", fontSize: 15 }}>{formatMoney(dir.custo_total)}</Text>
                </div>
              }
            >
              <Table
                columns={[
                  {
                    title: "Projecto",
                    dataIndex: "titulo",
                    key: "titulo",
                    render: (v, r) => (
                      <div>
                        <div style={{ fontWeight: 600 }}>{v}</div>
                        <Text type="secondary" style={{ fontSize: 11 }}>Início: {formatDate(r.inicio)}</Text>
                      </div>
                    ),
                  },
                  {
                    title: "Horas Efectivas",
                    dataIndex: "horas_efectivas",
                    key: "horas",
                    align: "right",
                    render: (v) => <Space><FaClock style={{ color: "#8c8c8c" }} /><Text>{formatHoras(v)}</Text></Space>,
                  },
                  {
                    title: "Custo Total",
                    dataIndex: "custo_total",
                    key: "custo",
                    align: "right",
                    render: (v) => <Text strong style={{ color: "#7c3aed" }}>{formatMoney(v)}</Text>,
                  },
                ]}
                dataSource={dir.projetos}
                rowKey="id_projeto"
                pagination={false}
                size="small"
                locale={{ emptyText: "Sem projectos" }}
                style={{ marginTop: 4 }}
              />
            </Panel>
          ))}
        </Collapse>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   ABA: PROJECTOS
───────────────────────────────────────────────────────────────────────────── */
const TabProjectos = ({ dataInicio, dataFim }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedProj, setExpandedProj] = useState([]);

  const load = useCallback(async () => {
    if (!dataInicio || !dataFim) return;
    try {
      setLoading(true);
      const res = await Api.ListCustoProjeto({ dataInicio, dataFim });
      setData(Array.isArray(res?.data) ? res.data : []);
    } catch {
      toast.error("Erro ao carregar custos por projecto.");
    } finally {
      setLoading(false);
    }
  }, [dataInicio, dataFim]);

  useEffect(() => { load(); }, [load]);

  const filtered = data.filter((p) =>
    !search || p.titulo?.toLowerCase().includes(search.toLowerCase()) ||
    p.direcao?.toLowerCase().includes(search.toLowerCase())
  );

  const totalCusto = filtered.reduce((a, p) => a + (p.custo_total || 0), 0);
  const totalHoras = filtered.reduce((a, p) => a + (p.horas_efectivas || 0), 0);

  if (loading) return <div style={{ textAlign: "center", padding: 60 }}><Spin size="large" /></div>;

  return (
    <div>
      <Row gutter={[20, 20]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <MetricCard title="Custo Total" value={formatMoney(totalCusto)} icon={<FaDollarSign />} iconBg="#e6fffb" iconColor="#13c2c2" subtitle={`${filtered.length} projecto(s)`} />
        </Col>
        <Col xs={24} sm={8}>
          <MetricCard title="Horas Efectivas" value={formatHoras(totalHoras)} icon={<FaClock />} iconBg="#e6f4ff" iconColor="#1677ff" subtitle="Total no período" />
        </Col>
        <Col xs={24} sm={8}>
          <MetricCard title="Projectos" value={filtered.length} icon={<FaProjectDiagram />} iconBg="#fff7e6" iconColor="#fa8c16" subtitle="Com custo registado" />
        </Col>
      </Row>

      <div style={{ marginBottom: 16 }}>
        <Input
          prefix={<FaSearch style={{ color: "#bfbfbf" }} />}
          placeholder="Pesquisar projecto ou direcção..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ borderRadius: 20, maxWidth: 340 }}
        />
      </div>

      {filtered.length === 0 ? (
        <Empty description="Nenhum projecto encontrado" />
      ) : (
        <Collapse
          activeKey={expandedProj}
          onChange={setExpandedProj}
          expandIconPosition="end"
          style={{ background: "transparent", border: "none" }}
        >
          {filtered.map((proj) => (
            <Panel
              key={proj.id_projeto}
              style={{
                marginBottom: 16,
                borderRadius: 14,
                border: "1px solid #e8e8f0",
                background: "#fff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                overflow: "hidden",
              }}
              header={
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingRight: 16 }}>
                  <Space>
                    <Avatar shape="square" style={{ background: "#13c2c222", borderRadius: 8, color: "#13c2c2" }} icon={<FaProjectDiagram />} />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{proj.titulo}</div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {proj.direcao} · {formatHoras(proj.horas_efectivas)} · {proj.etapas?.length || 0} actividade(s)
                      </Text>
                    </div>
                  </Space>
                  <Text strong style={{ color: "#13c2c2", fontSize: 15 }}>{formatMoney(proj.custo_total)}</Text>
                </div>
              }
            >
              {proj.etapas?.map((etapa) => (
                <div key={etapa.id_etapa} style={{ marginBottom: 20 }}>
                  <div
                    style={{
                      background: "#f8f0ff",
                      borderRadius: 10,
                      padding: "10px 16px",
                      marginBottom: 10,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Space>
                      <FaLayerGroup style={{ color: "#722ed1" }} />
                      <Text strong style={{ color: "#722ed1" }}>{etapa.descricao}</Text>
                    </Space>
                    <Space size="large">
                      <Text style={{ fontSize: 12, color: "#8c8c8c" }}>
                        <FaClock style={{ marginRight: 4 }} />{formatHoras(etapa.horas_efectivas)}
                      </Text>
                      <Text strong style={{ color: "#722ed1" }}>{formatMoney(etapa.custo_total)}</Text>
                    </Space>
                  </div>

                  <Table
                    columns={[
                      {
                        title: "Tarefa",
                        dataIndex: "titulo",
                        key: "titulo",
                        render: (v, r) => (
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{v}</div>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {formatDate(r.inicio)} → {formatDate(r.datafecho)}
                            </Text>
                          </div>
                        ),
                      },
                      {
                        title: "Tempo",
                        key: "tempo",
                        align: "center",
                        render: (_, r) => (
                          <Space direction="vertical" size={0} style={{ textAlign: "center" }}>
                            <Text style={{ fontSize: 12 }}>{formatMinutos(r.minutos_efectivos)} efectivos</Text>
                            {r.minutos_suspensos > 0 && (
                              <Text type="secondary" style={{ fontSize: 11 }}>
                                <FaPauseCircle style={{ color: "#fa8c16", marginRight: 3 }} />
                                {formatMinutos(r.minutos_suspensos)} suspensos
                              </Text>
                            )}
                          </Space>
                        ),
                      },
                      {
                        title: "Responsáveis",
                        dataIndex: "responsaveis",
                        key: "resp",
                        render: (resp) => (
                          <Avatar.Group maxCount={3} size="small">
                            {resp?.map((r) => (
                              <Tooltip key={r.id_user} title={`${r.nome} · ${formatHoras(r.horas)} · ${formatMoney(r.custo)}`}>
                                <Avatar style={{ backgroundColor: "#1677ff", fontSize: 10 }}>
                                  {r.nome?.charAt(0)}
                                </Avatar>
                              </Tooltip>
                            ))}
                          </Avatar.Group>
                        ),
                      },
                      {
                        title: "Custo Total",
                        dataIndex: "custo_total",
                        key: "custo",
                        align: "right",
                        render: (v) => <Text strong style={{ color: "#13c2c2" }}>{formatMoney(v)}</Text>,
                      },
                    ]}
                    dataSource={etapa.tarefas}
                    rowKey="id_tarefa"
                    pagination={false}
                    size="small"
                    locale={{ emptyText: "Sem tarefas" }}
                    expandable={{
                      expandedRowRender: (tarefa) => (
                        <div style={{ padding: "8px 16px" }}>
                          <Text strong style={{ fontSize: 12, color: "#8c8c8c" }}>Custo por colaborador:</Text>
                          <Row gutter={[10, 10]} style={{ marginTop: 8 }}>
                            {tarefa.responsaveis?.map((r) => (
                              <Col key={r.id_user} xs={24} sm={12} md={8}>
                                <Card size="small" style={{ borderRadius: 10 }}>
                                  <Space>
                                    <Avatar style={{ backgroundColor: "#1677ff", fontSize: 12 }}>{r.nome?.charAt(0)}</Avatar>
                                    <div>
                                      <div style={{ fontWeight: 600, fontSize: 13 }}>{r.nome}</div>
                                      <Text type="secondary" style={{ fontSize: 11 }}>
                                        {formatHoras(r.horas)} × {formatMoney(r.salario_hora)}/h
                                      </Text>
                                      <div style={{ fontWeight: 700, color: "#13c2c2", fontSize: 13 }}>{formatMoney(r.custo)}</div>
                                    </div>
                                  </Space>
                                </Card>
                              </Col>
                            ))}
                          </Row>
                        </div>
                      ),
                      rowExpandable: (r) => r.responsaveis?.length > 0,
                    }}
                  />
                </div>
              ))}
            </Panel>
          ))}
        </Collapse>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   ABA: COLABORADOR
───────────────────────────────────────────────────────────────────────────── */
const TabColaborador = ({ dataInicio, dataFim, colaboradores, loadingColabs }) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (userId) => {
    if (!userId || !dataInicio || !dataFim) return;
    try {
      setLoading(true);
      setData(null);
      const res = await Api.ListCustoUsuario({ id: userId, dataInicio, dataFim });
      setData(res?.data || null);
    } catch {
      toast.error("Erro ao carregar custos do colaborador.");
    } finally {
      setLoading(false);
    }
  }, [dataInicio, dataFim]);

  const handleSelect = (val) => {
    setSelectedUser(val);
    load(val);
  };

  // reload when dates change
  useEffect(() => {
    if (selectedUser) load(selectedUser);
  }, [dataInicio, dataFim]);

  return (
    <div>
      {/* Selector */}
      <Card
        bordered={false}
        style={{ borderRadius: 14, marginBottom: 24, background: "#f8faff", border: "1px solid #e6f0ff" }}
        bodyStyle={{ padding: "16px 20px" }}
      >
        <Row align="middle" gutter={16}>
          <Col>
            <FaUserTie style={{ color: "#1677ff", fontSize: 20 }} />
          </Col>
          <Col flex={1}>
            <Select
              showSearch
              placeholder="Selecione um colaborador..."
              style={{ width: "100%", maxWidth: 400 }}
              value={selectedUser}
              onChange={handleSelect}
              loading={loadingColabs}
              optionFilterProp="children"
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {colaboradores.map((c) => (
                <Option key={c.id} value={c.id}>{c.nome}</Option>
              ))}
            </Select>
          </Col>
          {selectedUser && (
            <Col>
              <Button
                size="small"
                onClick={() => { setSelectedUser(null); setData(null); }}
                style={{ borderRadius: 20 }}
              >
                Limpar
              </Button>
            </Col>
          )}
        </Row>
      </Card>

      {!selectedUser && (
        <Empty description="Seleccione um colaborador para ver os detalhes de custo" />
      )}

      {loading && (
        <div style={{ textAlign: "center", padding: 60 }}>
          <Spin size="large" tip="Carregando dados do colaborador..." />
        </div>
      )}

      {data && !loading && (
        <>
          {/* Profile Card */}
          <Card
            bordered={false}
            style={{
              borderRadius: 16,
              marginBottom: 24,
              background: "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)",
              boxShadow: "0 8px 20px rgba(22,119,255,0.25)",
            }}
            bodyStyle={{ padding: "24px 28px" }}
          >
            <Row align="middle" gutter={24}>
              <Col>
                <Avatar
                  size={64}
                  style={{ background: "rgba(255,255,255,0.25)", fontSize: 28, fontWeight: 700, border: "2px solid rgba(255,255,255,0.5)" }}
                >
                  {data.utilizador?.nome?.charAt(0)}
                </Avatar>
              </Col>
              <Col flex={1}>
                <Title level={3} style={{ color: "#fff", margin: 0 }}>{data.utilizador?.nome}</Title>
                <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 14 }}>
                  {data.utilizador?.funcao} · {data.utilizador?.email}
                </Text>
                <div style={{ marginTop: 4 }}>
                  <Tag style={{ background: "rgba(255,255,255,0.2)", color: "#fff", border: "1px solid rgba(255,255,255,0.35)", borderRadius: 20 }}>
                    Salário/hora: {formatMoney(data.utilizador?.salario_hora)}
                  </Tag>
                </div>
              </Col>
            </Row>
          </Card>

          {/* Resumo metrics */}
          <Row gutter={[20, 20]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={8}>
              <MetricCard title="Custo Total" value={formatMoney(data.resumo?.custo_total)} icon={<FaDollarSign />} iconBg="#e6fffb" iconColor="#13c2c2" subtitle="No período selecionado" />
            </Col>
            <Col xs={24} sm={8}>
              <MetricCard title="Horas Efectivas" value={data.resumo?.formatado || "—"} icon={<FaClock />} iconBg="#e6f4ff" iconColor="#1677ff" subtitle={`${formatMinutos(data.resumo?.minutos_efectivos)} efectivos`} />
            </Col>
            <Col xs={24} sm={8}>
              <MetricCard title="Tarefas" value={data.resumo?.total_tarefas || 0} icon={<FaTasks />} iconBg="#fff7e6" iconColor="#fa8c16" subtitle="No período" />
            </Col>
          </Row>

          {/* Tarefas */}
          <Card
            bordered={false}
            style={{ borderRadius: 16, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}
            title={
              <Space>
                <FaTasks style={{ color: "#13c2c2" }} />
                <span style={{ fontWeight: 600 }}>Tarefas no Período</span>
              </Space>
            }
            bodyStyle={{ padding: 0 }}
          >
            <Table
              columns={[
                {
                  title: "Tarefa",
                  dataIndex: "titulo",
                  key: "titulo",
                  render: (v, r) => (
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{v}</div>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {formatDate(r.inicio)} → {formatDate(r.datafecho)}
                      </Text>
                    </div>
                  ),
                },
                {
                  title: "Tempo",
                  key: "tempo",
                  align: "center",
                  render: (_, r) => (
                    <Space direction="vertical" size={0} style={{ textAlign: "center" }}>
                      <Text strong>{r.formatado}</Text>
                      {r.minutos_suspensos > 0 && (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          <FaPauseCircle style={{ color: "#fa8c16", marginRight: 2 }} />
                          {formatMinutos(r.minutos_suspensos)} suspenso
                        </Text>
                      )}
                    </Space>
                  ),
                },
                {
                  title: "Custo",
                  dataIndex: "custo",
                  key: "custo",
                  align: "right",
                  sorter: (a, b) => a.custo - b.custo,
                  render: (v) => (
                    <Text strong style={{ color: "#1677ff", fontSize: 14 }}>{formatMoney(v)}</Text>
                  ),
                },
              ]}
              dataSource={data.tarefas}
              rowKey="id_tarefa"
              pagination={{ pageSize: 8, showTotal: (t) => `Total: ${t} tarefas` }}
              locale={{ emptyText: "Sem tarefas no período" }}
              size="middle"
              expandable={{
                expandedRowRender: (tarefa) =>
                  tarefa.suspensoes?.length > 0 ? (
                    <div style={{ padding: "10px 20px", background: "#fffbe6", borderRadius: 8 }}>
                      <Text strong style={{ fontSize: 12, color: "#fa8c16" }}>
                        <FaPauseCircle style={{ marginRight: 6 }} />
                        Suspensões ({tarefa.suspensoes.length})
                      </Text>
                      <Table
                        size="small"
                        pagination={false}
                        style={{ marginTop: 8 }}
                        columns={[
                          { title: "Descrição", dataIndex: "descricao", key: "desc" },
                          { title: "Entrada", dataIndex: "entrada", key: "entrada", render: (v) => formatDate(v) },
                          { title: "Saída", dataIndex: "saida", key: "saida", render: (v) => formatDate(v) },
                          { title: "Minutos", dataIndex: "minutos", key: "min", render: (v) => `${v}min` },
                        ]}
                        dataSource={tarefa.suspensoes}
                        rowKey="id"
                      />
                    </div>
                  ) : <Text type="secondary" style={{ padding: "8px 20px", display: "block" }}>Sem suspensões registadas</Text>,
                rowExpandable: (r) => true,
              }}
              summary={(pageData) => {
                const total = pageData.reduce((a, r) => a + (r.custo || 0), 0);
                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row style={{ background: "#f0f9ff" }}>
                      <Table.Summary.Cell index={0} colSpan={2}>
                        <Text strong>Total da página</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right">
                        <Text strong style={{ color: "#1677ff", fontSize: 15 }}>{formatMoney(total)}</Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
          </Card>
        </>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   SCREEN PRINCIPAL
───────────────────────────────────────────────────────────────────────────── */
const RelatorioCustos = () => {
  const navigate = useNavigate();

  const [dateRange, setDateRange] = useState([
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ]);
  const [activeTab, setActiveTab] = useState("projectos");
  const [colaboradores, setColaboradores] = useState([]);
  const [loadingColabs, setLoadingColabs] = useState(false);

  const dataInicio = dateRange?.[0]?.format("YYYY-MM-DD");
  const dataFim = dateRange?.[1]?.format("YYYY-MM-DD");

  // Load colaboradores list (assuming Api.ListUsuarios or similar)
  useEffect(() => {
    const fetchColabs = async () => {
      try {
        setLoadingColabs(true);
        const res = await Api.ListUser();
        const arr = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        setColaboradores(arr);
      } catch {
        // silently fail; the select will just be empty
      } finally {
        setLoadingColabs(false);
      }
    };
    fetchColabs();
    document.title = "Relatório de Custos";
  }, []);

  return (
    <Layout style={{ background: "transparent" }}>
      <Content style={{ padding: "24px 32px" }}>
        <ToastContainer position="top-right" autoClose={4000} />

        {/* ── Header ── */}
        <div
          style={{
            background: "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)",
            borderRadius: 16,
            padding: "24px 32px",
            marginBottom: 32,
            boxShadow: "0 8px 20px rgba(22,119,255,0.2)",
          }}
        >
          <Row align="middle" justify="space-between" gutter={[16, 16]}>
            <Col>
              <Space size="middle">
                <div
                  style={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    borderRadius: 12,
                    padding: 10,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <FaChartPie style={{ fontSize: 32, color: "#fff" }} />
                </div>
                <div>
                  <Title level={2} style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
                    Relatório de Custos
                  </Title>
                  <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 15 }}>
                    Análise financeira por período, direcção, projecto e colaborador
                  </Text>
                </div>
              </Space>
            </Col>
            <Col>
              <Row gutter={12} align="middle">
                <Col>
                  <Space direction="vertical" size={2}>
                    <Text style={{ color: "rgba(255,255,255,0.75)", fontSize: 12 }}>
                      <FaCalendarAlt style={{ marginRight: 4 }} />
                      Período
                    </Text>
                    <RangePicker
                      value={dateRange}
                      onChange={setDateRange}
                      format="DD/MM/YYYY"
                      style={{ borderRadius: 20 }}
                      allowClear={false}
                    />
                  </Space>
                </Col>
 
              </Row>
            </Col>
          </Row>
        </div>

        {/* ── Tabs ── */}
        <Card
          bordered={false}
          style={{ borderRadius: 16, boxShadow: "0 4px 16px rgba(0,0,0,0.05)" }}
          bodyStyle={{ padding: "0 0 24px 0" }}
        >
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            size="large"
            tabBarStyle={{ padding: "0 24px", marginBottom: 0, borderBottom: "1px solid #f0f0f0" }}
            tabBarGutter={32}
            items={[
              {
                key: "projectos",
                label: (
                  <Space>
                    <FaProjectDiagram style={{ color: activeTab === "projectos" ? "#13c2c2" : "#8c8c8c" }} />
                    <span>Por Projecto</span>
                  </Space>
                ),
                children: (
                  <div style={{ padding: "24px 24px 0" }}>
                    <TabProjectos dataInicio={dataInicio} dataFim={dataFim} />
                  </div>
                ),
              },
              {
                key: "direcao",
                label: (
                  <Space>
                    <FaBuilding style={{ color: activeTab === "direcao" ? "#7c3aed" : "#8c8c8c" }} />
                    <span>Por Direcção</span>
                  </Space>
                ),
                children: (
                  <div style={{ padding: "24px 24px 0" }}>
                    <TabDirecao dataInicio={dataInicio} dataFim={dataFim} />
                  </div>
                ),
              },
              {
                key: "colaborador",
                label: (
                  <Space>
                    <FaUserTie style={{ color: activeTab === "colaborador" ? "#1677ff" : "#8c8c8c" }} />
                    <span>Por Colaborador</span>
                  </Space>
                ),
                children: (
                  <div style={{ padding: "24px 24px 0" }}>
                    <TabColaborador
                      dataInicio={dataInicio}
                      dataFim={dataFim}
                      colaboradores={colaboradores}
                      loadingColabs={loadingColabs}
                    />
                  </div>
                ),
              },
            ]}
          />
        </Card>
      </Content>
    </Layout>
  );
};

export default RelatorioCustos;